import { useCallback } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { looseValueEqual } from '@cbridge-utils/shared-helpers';

const getByPath = (obj: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>(
    (acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]),
    obj,
  );

// Per-field revert: a field is "dirty" when its current value differs (loosely) from
// its baseline, and reverting sets it back. The baseline is the value the form loaded
// with, unless the field supplies its own `revertValue` (e.g. a row added after load).
export const useFieldRevert = (name: string, revertValue?: unknown) => {
  const { resetField, setValue } = useFormContext();
  const { defaultValues } = useFormState({ name });
  const value = useWatch({ name });

  const hasRevertValue = revertValue !== undefined;
  const baseline = hasRevertValue ? revertValue : getByPath(defaultValues, name);
  const isDirty = !looseValueEqual(value, baseline);

  const revert = useCallback(() => {
    if (hasRevertValue) {
      setValue(name, revertValue, { shouldDirty: true, shouldValidate: true });
    } else {
      resetField(name);
    }
  }, [hasRevertValue, name, resetField, revertValue, setValue]);

  return { isDirty, revert };
};
